<template>
  <div
    class="flex w-full flex-col space-y-4"
    :class="model.type === NODE_TYPE_TRANSITION ? 'p-2' : 'border-l p-4'"
  >
    <!-- service header -->
    <div class="flex justify-between">
      <div class="flex items-center pt-0.5">
        <icon-base
          v-if="model.type !== 'orchestrations'"
          :icon="model.stageType ? STAGE_ICONS[model.stageType].icon : IconCircle"
          :class="
            model.stageType
              ? `mr-1 -ml-1 ${STAGE_ICONS[model.stageType].color}`
              : 'mt-1'
          "
          :icon-color="model.stageType ? undefined : serviceColor"
        />
        <lf-heading4
          class="mr-2 max-w-55 overflow-hidden whitespace-pre-wrap"
          data-cy="service-info-provider-title"
        >
          {{ model.title }}
        </lf-heading4>
        <div
          v-if="model.info"
          v-tooltip="{
            content: model.info,
            placement: 'bottom',
          }"
          class="relative cursor-pointer"
        >
          <icon-base :icon="IconInfoSmall" :icon-color="UTIL_COLORS.DEFAULT" />
        </div>
      </div>

      <router-link
        v-if="model.type === 'orchestrations'"
        :to="{
          name: ROUTE_ORCHESTRATION_TEMPLATE,
          params: { id: model.id },
        }"
        target="_blank"
        class="px-1 hover:text-primary"
      >
        <icon-base :icon="IconPopup" />
      </router-link>
    </div>
    <!-- available services -->
    <div v-if="type === 'all_data_services'" class="flex flex-wrap">
      <div
        v-for="service in model.services"
        :key="service.id"
        class="mr-1 mb-1 flex h-7 items-baseline space-x-2 rounded-sm border bg-white px-4 py-2"
      >
        <img
          :src="getImageForService(service.id) || undefined"
          class="h-2-5 w-2-5"
          loading="lazy"
        />
        <span
          class="text-xxs font-semibold whitespace-nowrap uppercase"
          data-cy="service-info-name"
        >
          {{ service.name }}
        </span>
      </div>
    </div>
    <div
      v-else-if="type === 'orchestrations'"
      class="flex flex-wrap gap-x-2 gap-y-3"
    >
      <template
        v-for="serviceId in model.assignedOrchestrationServices"
        :key="serviceId"
      >
        <img
          v-if="getImageForService(serviceId)"
          :src="getImageForService(serviceId)"
          class="h-2-5 w-2-5"
          loading="lazy"
        />
      </template>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, defineAsyncComponent } from "vue";

import {
  COLORS,
  SERVICES_COLOR_CODES,
  UTIL_COLORS,
  WORKFLOW_STAGES,
} from "@/helpers/constants";
import { NODE_TYPE_TRANSITION } from "@/helpers/constants/orchestration";
import { getImageForService } from "@/helpers/services";
import { ROUTE_ORCHESTRATION_TEMPLATE } from "@/router/routes";

import IconCircle from "@/components/icons/IconCircle.vue";
import IconFullApplication from "@/components/icons/IconFullApplication.vue";
import IconInfoSmall from "@/components/icons/IconInfoSmall.vue";
import IconOffer from "@/components/icons/IconOffer.vue";
import IconOrchestration from "@/components/icons/IconOrchestration.vue";
import IconPopup from "@/components/icons/IconPopup.vue";
import IconAccount from "@/components/icons/sidebar/profile/IconAccount.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfHeading4 from "@/components/ui/text/LfHeading4.vue";

import type { IDataService } from "@/models/orchestration";
import type { PropType } from "vue";

const IconScoreCardsGroup = defineAsyncComponent(
  () => import("@/components/icons/IconScoreCardsGroup.vue")
);

const props = defineProps({
  model: {
    type: Object as PropType<IDataService>,
    default: null,
  },
  type: {
    type: String,
    default: "",
  },
});

const STAGE_ICONS = {
  [WORKFLOW_STAGES.APPLICATION]: {
    icon: IconFullApplication,
    color: "application-text",
  },
  [WORKFLOW_STAGES.UNDERWRITING]: {
    icon: IconOrchestration,
    color: "orchestration-text",
  },
  [WORKFLOW_STAGES.SCORECARDS]: {
    icon: IconScoreCardsGroup,
    color: "scorecards-text",
  },
  [WORKFLOW_STAGES.OFFER]: {
    icon: IconOffer,
    color: "offer-text",
  },
  [WORKFLOW_STAGES.PLACEMENT]: {
    icon: IconAccount,
    color: "placement-text",
  },
};

const serviceColor = computed(
  () =>
    `rgb(${
      COLORS[
        SERVICES_COLOR_CODES[
          props.model.title as keyof typeof SERVICES_COLOR_CODES
        ]
      ]
    })`
);
</script>
<style>
@reference "@/assets/tailwind.css";

.orchestration-text {
  @apply text-violet-400;
}
.application-text {
  @apply text-teal-400;
}
.scorecards-text {
  @apply text-orange-400;
}
.offer-text {
  @apply text-blue-400;
}
.placement-text {
  @apply text-blue-300;
}
</style>
